/**
 * Email thread "Initiated by" cell.
 *
 * Renders the initiator as an entity chip when the
 * `ahoy_email_thread_to_initiator` association resolves to a contact or user,
 * otherwise the plain `ahoy_initiator_email` text. Unlike a generic association
 * cell, an unresolved initiator must show as plain text rather than an empty
 * dash or a create-contact affordance — the initiator may be an internal user,
 * not always a contact.
 *
 * When the association points at a target that cannot be hydrated (a delete
 * race or a fetch/404 failure), it falls back to the always-populated plain
 * `ahoy_initiator_email` rather than letting `EntityBadgeByIds` render its
 * failed-state pill. A still-in-flight fetch keeps the badge's own loading
 * state; only a settled failure with no resolved object falls back.
 */

import { useMemo, useSyncExternalStore } from 'react';

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { EntityBadgeByIds } from '../../components/badges';
import { PropertyValueLabel } from '../../components/properties/labels';
import { useCrmObjectSnapshots } from '../../hooks/useCrmObjectSnapshots';
import type { PropertyTypeSchema } from '../../hooks/usePropertySchema';
import {
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '../../object-cards/entityFetchBatcher';

const INITIATOR_ASSOCIATION_TYPE_NAME = 'ahoy_email_thread_to_initiator';
const EMPTY_ENTITY_IDS: readonly string[] = [];

export interface EmailInitiatorCellProps {
  object: CrmObject;
  propertySchemas: Record<string, PropertyTypeSchema>;
  orgId: number | undefined;
}

export function EmailInitiatorCell({
  object,
  propertySchemas,
  orgId,
}: EmailInitiatorCellProps): React.ReactNode {
  const targetId = object.associations?.find(
    a => a.association_type_name === INITIATOR_ASSOCIATION_TYPE_NAME
  )?.target_entity_id;

  const entityIds = useMemo(
    () => (targetId ? [targetId] : EMPTY_ENTITY_IDS),
    [targetId]
  );
  const snapshots = useCrmObjectSnapshots(entityIds, orgId);
  const failureVersion = useSyncExternalStore(
    subscribeToFetchFailures,
    getFailureVersion
  );

  const showEmailFallback = useMemo(() => {
    if (!targetId || !orgId || orgId <= 0) {
      return true;
    }
    // Hydration settled without resolving the target (deleted entity / 404).
    return !snapshots.get(targetId) && hasFailedFetch(targetId);
    // failureVersion is intentionally listed: hasFailedFetch() reads from a
    // store outside React, so we recompute when its version bumps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, orgId, snapshots, failureVersion]);

  // `orgId` is guaranteed a positive number here (showEmailFallback is true
  // whenever it isn't), but narrow explicitly so the badge's `orgId: number`
  // prop type is satisfied.
  if (showEmailFallback || !orgId) {
    return (
      <PropertyValueLabel
        value={getPropertyValue(object.properties?.ahoy_initiator_email)}
        propertyType={propertySchemas.ahoy_initiator_email}
        fontSize='table'
        truncate
      />
    );
  }

  return (
    <EntityBadgeByIds
      entityIds={entityIds}
      orgId={orgId}
      variant='xs'
      maxVisible={1}
      wrap={false}
    />
  );
}
